import { useState, useEffect, useMemo } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Download, X, Eye, AlertTriangle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ServerPagination } from "@/components/ui/server-pagination";
import { exportToCsv } from "@/lib/exportCsv";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

const SessionSummaryAuditLog = () => {
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUserSearch, setDebouncedUserSearch] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [debouncedSessionId, setDebouncedSessionId] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [detailSessionId, setDetailSessionId] = useState<string | null>(null);

  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ["session-detail", detailSessionId],
    enabled: !!detailSessionId,
    queryFn: async () => {
      const [summaryRes, sessionRes, recordsRes] = await Promise.all([
        supabase.from("session_summaries").select("*").eq("session_id", detailSessionId!).maybeSingle(),
        supabase.from("attendance_sessions").select("*").eq("id", detailSessionId!).maybeSingle(),
        supabase
          .from("attendance_records")
          .select("id, status, scanned_at, notes, rejection_reason, student_id, students(first_name, last_name, student_number)")
          .eq("session_id", detailSessionId!)
          .order("scanned_at", { ascending: true }),
      ]);
      return {
        summary: summaryRes.data,
        session: sessionRes.data,
        records: recordsRes.data ?? [],
      };
    },
  });

  const recordsByStatus = useMemo(() => {
    const map: Record<string, any[]> = {};
    for (const r of (detailData?.records ?? []) as any[]) {
      const k = r.status || "unknown";
      (map[k] ||= []).push(r);
    }
    return map;
  }, [detailData]);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedUserSearch(userSearch); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [userSearch]);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSessionId(sessionId); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [sessionId]);

  useEffect(() => { setCurrentPage(1); }, [fromDate, toDate, classFilter]);

  const { data: profiles = [] } = useQuery({
    queryKey: ["audit-profiles-all"],
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("user_id, full_name");
      return data ?? [];
    },
  });

  const profileMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of profiles as any[]) {
      if (p.user_id) m.set(p.user_id, p.full_name || "");
    }
    return m;
  }, [profiles]);

  const matchedUserIds = useMemo(() => {
    if (!debouncedUserSearch.trim()) return null;
    const q = debouncedUserSearch.trim().toLowerCase();
    return (profiles as any[])
      .filter((p) => (p.full_name || "").toLowerCase().includes(q))
      .map((p) => p.user_id);
  }, [debouncedUserSearch, profiles]);

  const { data, isLoading } = useQuery({
    queryKey: [
      "session-summary-audit",
      debouncedSessionId,
      classFilter,
      fromDate,
      toDate,
      matchedUserIds,
      currentPage,
      pageSize,
    ],
    queryFn: async () => {
      let q = supabase
        .from("audit_logs")
        .select("*", { count: "exact" })
        .in("action", ["generate_session_summary", "regenerate_session_summary"])
        .eq("table_name", "session_summaries")
        .order("created_at", { ascending: false });

      if (fromDate) q = q.gte("created_at", new Date(fromDate).toISOString());
      if (toDate) {
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);
        q = q.lte("created_at", end.toISOString());
      }
      if (debouncedSessionId.trim()) {
        q = q.filter("new_data->>session_id", "eq", debouncedSessionId.trim());
      }
      if (classFilter !== "all") {
        q = q.filter("new_data->>class", "eq", classFilter);
      }
      if (matchedUserIds) {
        if (matchedUserIds.length === 0) return { logs: [], totalCount: 0 };
        q = q.in("user_id", matchedUserIds);
      }

      const from = (currentPage - 1) * pageSize;
      q = q.range(from, from + pageSize - 1);

      const { data, error, count } = await q;
      if (error) throw error;
      return { logs: data ?? [], totalCount: count ?? 0 };
    },
  });

  const logs = data?.logs ?? [];
  const totalCount = data?.totalCount ?? 0;

  const { data: classOptions = [] } = useQuery({
    queryKey: ["session-summary-audit-classes"],
    queryFn: async () => {
      const { data } = await supabase
        .from("session_summaries")
        .select("class")
        .not("class", "is", null);
      const unique = Array.from(new Set((data ?? []).map((r: any) => r.class).filter(Boolean))) as string[];
      return unique.sort();
    },
  });

  const formatDateTime = (t: string) => new Date(t).toLocaleString();

  const clearFilters = () => {
    setUserSearch(""); setSessionId(""); setClassFilter("all"); setFromDate(""); setToDate("");
  };

  const hasFilters = userSearch || sessionId || classFilter !== "all" || fromDate || toDate;

  const handleExport = () => {
    const headers = [
      "Generated At", "Generated By", "Session ID", "Class", "Module", "Topic", "Session Date",
      "Total Enrolled", "Present", "Pending", "AO Pending", "HOD Pending", "Rejected", "Absent", "Attendance Rate"
    ];
    const rows = logs.map((log: any) => {
      const d = log.new_data || {};
      return [
        formatDateTime(log.created_at),
        profileMap.get(log.user_id) || log.user_id || "System",
        d.session_id || "",
        d.class || "",
        d.module_code || "",
        d.topic || "",
        d.session_date || "",
        String(d.total_enrolled ?? ""),
        String(d.present_count ?? ""),
        String(d.pending_count ?? ""),
        String(d.pending_approval_count ?? ""),
        String(d.pending_hod_confirm_count ?? ""),
        String(d.rejected_count ?? ""),
        String(d.absent_count ?? ""),
        d.attendance_rate != null ? `${(Number(d.attendance_rate) * 100).toFixed(1)}%` : "",
      ];
    });
    exportToCsv(`session-summary-audit-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  return (
    <AppLayout>
      <div className="space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
          <div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold">Session Summary Audit</h1>
            <p className="text-muted-foreground text-xs sm:text-sm">
              Track every session summary generation with full metric snapshots.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={logs.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Filters</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">From date</Label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">To date</Label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">User (name)</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name..."
                    className="pl-9"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Session ID</Label>
                <Input
                  placeholder="UUID"
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Class</Label>
                <Select value={classFilter} onValueChange={setClassFilter}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All classes</SelectItem>
                    {classOptions.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {hasFilters && (
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Clear filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Generation Events</CardTitle>
            <CardDescription>{totalCount} entries</CardDescription>
          </CardHeader>
          <CardContent className="table-responsive">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Generated at</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Generated by</TableHead>
                  <TableHead>Session</TableHead>
                  <TableHead>Class / Module</TableHead>
                  <TableHead className="text-right">Enrolled</TableHead>
                  <TableHead className="text-right">Present</TableHead>
                  <TableHead className="text-right">Pending</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log: any) => {
                  const d = log.new_data || {};
                  const old = log.old_data || null;
                  const isRegen = log.action === "regenerate_session_summary";
                  const presentDelta = old ? (d.present_count ?? 0) - (old.present_count ?? 0) : null;

                  // Anomaly detection
                  const anomalies: string[] = [];
                  const rate = d.attendance_rate != null ? Number(d.attendance_rate) : null;
                  if (d.session_id == null) anomalies.push("Missing session ID");
                  if (d.class == null && d.module_code == null) anomalies.push("Missing class & module");
                  if (d.total_enrolled == null || d.total_enrolled === 0) anomalies.push("Zero enrolled students");
                  if (d.attendance_rate == null && d.total_enrolled > 0) anomalies.push("Missing attendance rate");
                  if (rate != null && (rate < 0 || rate > 1)) anomalies.push(`Out-of-range rate (${rate})`);
                  if (rate === 1 && (d.total_enrolled ?? 0) >= 5) anomalies.push("100% attendance — verify");
                  if (rate === 0 && (d.total_enrolled ?? 0) > 0 && (d.pending_count ?? 0) === 0) anomalies.push("0% attendance, no pending");
                  if ((d.present_count ?? 0) > (d.total_enrolled ?? 0)) anomalies.push("Present exceeds enrolled");
                  if (old && presentDelta !== null && Math.abs(presentDelta) >= 10) {
                    anomalies.push(`Large delta vs previous (${presentDelta > 0 ? "+" : ""}${presentDelta})`);
                  }

                  return (
                    <TableRow key={log.id}>
                      <TableCell className="text-xs whitespace-nowrap">{formatDateTime(log.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 items-start">
                          <Badge variant={isRegen ? "secondary" : "outline"} className="text-xs">
                            {isRegen ? "Regenerated" : "Generated"}
                          </Badge>
                          {anomalies.length > 0 && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="destructive" className="text-[10px] gap-1 cursor-help">
                                    <AlertTriangle className="h-3 w-3" />
                                    Anomaly
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="max-w-xs">
                                  <ul className="list-disc pl-4 space-y-0.5 text-xs">
                                    {anomalies.map((a, i) => <li key={i}>{a}</li>)}
                                  </ul>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-sm">
                        {profileMap.get(log.user_id) || (log.user_id ? log.user_id.slice(0, 8) : "System")}
                      </TableCell>
                      <TableCell className="max-w-[220px]">
                        <div className="text-sm truncate">{d.topic || "—"}</div>
                        <div className="text-xs text-muted-foreground font-mono truncate">
                          {d.session_id?.slice(0, 8) || "—"} · {d.session_date || ""}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {d.class && <Badge variant="outline" className="w-fit text-xs">{d.class}</Badge>}
                          {d.module_code && <span className="text-xs text-muted-foreground">{d.module_code}</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm">{d.total_enrolled ?? 0}</TableCell>
                      <TableCell className="text-right text-sm text-primary font-medium">
                        {d.present_count ?? 0}
                        {presentDelta !== null && presentDelta !== 0 && (
                          <div className={`text-[10px] font-normal ${presentDelta > 0 ? "text-primary" : "text-destructive"}`}>
                            {presentDelta > 0 ? "+" : ""}{presentDelta} vs prev
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {d.pending_count ?? 0}
                        {(d.pending_approval_count > 0 || d.pending_hod_confirm_count > 0) && (
                          <div className="text-[10px] text-muted-foreground">
                            AO {d.pending_approval_count ?? 0} · HOD {d.pending_hod_confirm_count ?? 0}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium">
                        {d.attendance_rate != null ? `${(Number(d.attendance_rate) * 100).toFixed(1)}%` : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDetailSessionId(d.session_id)}
                          disabled={!d.session_id}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {logs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                      {isLoading ? "Loading..." : "No summary generations found for the selected filters"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <ServerPagination
              currentPage={currentPage}
              pageSize={pageSize}
              totalCount={totalCount}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!detailSessionId} onOpenChange={(o) => !o && setDetailSessionId(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Session details</DialogTitle>
            <DialogDescription>
              {detailData?.session?.topic || "Session"} · {detailData?.session?.session_date || ""}
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading...</div>
          ) : !detailData?.summary ? (
            <div className="py-8 text-center text-sm text-muted-foreground">No summary snapshot found for this session.</div>
          ) : (
            <ScrollArea className="flex-1 pr-3">
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                  <div className="rounded-md border p-2">
                    <div className="text-xs text-muted-foreground">Class</div>
                    <div className="font-medium">{detailData.summary.class || "—"}</div>
                  </div>
                  <div className="rounded-md border p-2">
                    <div className="text-xs text-muted-foreground">Module</div>
                    <div className="font-medium">{detailData.summary.module_code || "—"}</div>
                  </div>
                  <div className="rounded-md border p-2">
                    <div className="text-xs text-muted-foreground">Enrolled</div>
                    <div className="font-medium">{detailData.summary.total_enrolled}</div>
                  </div>
                  <div className="rounded-md border p-2">
                    <div className="text-xs text-muted-foreground">Attendance rate</div>
                    <div className="font-medium">
                      {detailData.summary.attendance_rate != null
                        ? `${(Number(detailData.summary.attendance_rate) * 100).toFixed(1)}%`
                        : "—"}
                    </div>
                  </div>
                  <div className="rounded-md border p-2">
                    <div className="text-xs text-muted-foreground">Present</div>
                    <div className="font-medium text-primary">{detailData.summary.present_count}</div>
                  </div>
                  <div className="rounded-md border p-2">
                    <div className="text-xs text-muted-foreground">Absent</div>
                    <div className="font-medium">{detailData.summary.absent_count}</div>
                  </div>
                  <div className="rounded-md border p-2">
                    <div className="text-xs text-muted-foreground">Pending (AO/HOD)</div>
                    <div className="font-medium">
                      {detailData.summary.pending_approval_count} / {detailData.summary.pending_hod_confirm_count}
                    </div>
                  </div>
                  <div className="rounded-md border p-2">
                    <div className="text-xs text-muted-foreground">Rejected</div>
                    <div className="font-medium">{detailData.summary.rejected_count}</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="text-sm font-semibold">Attendance breakdown ({detailData.records.length} records)</div>
                  {Object.keys(recordsByStatus).length === 0 && (
                    <div className="text-sm text-muted-foreground">No attendance records.</div>
                  )}
                  {Object.entries(recordsByStatus).map(([status, items]) => (
                    <div key={status} className="rounded-md border">
                      <div className="flex items-center justify-between px-3 py-2 bg-muted/40">
                        <Badge variant="outline" className="capitalize">{status.replace(/_/g, " ")}</Badge>
                        <span className="text-xs text-muted-foreground">{items.length}</span>
                      </div>
                      <div className="divide-y">
                        {items.map((r: any) => (
                          <div key={r.id} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs">
                            <div className="truncate">
                              <span className="font-medium">
                                {r.students?.first_name} {r.students?.last_name}
                              </span>
                              <span className="text-muted-foreground ml-2">{r.students?.student_number}</span>
                            </div>
                            <div className="text-muted-foreground whitespace-nowrap">
                              {r.scanned_at ? new Date(r.scanned_at).toLocaleTimeString() : ""}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default SessionSummaryAuditLog;
